import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Loader2,
  AlertCircle,
  Zap,
  CheckCircle,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Circle,
  AlertTriangle,
} from 'lucide-react';
import { config } from '../config';
import { getWorkspace, useWorkspace } from '../workspace';
import {
  createRemediationDoc,
  getRemediationByThreatEventId,
  updateRemediation,
  type RemediationItem,
} from '../api-utils/remediations';
import { createNotification } from '../api-utils/notifications';
import { updateThreatEvent } from '../api-utils/threats/update_threat_data';
import {
  getRemediationChatParams,
  getActiveWorkspace,
  buildChatMessagePayload,
  REMEDIATION_AGENT_CORE,
  REMEDIATION_CASE_GROUP,
  getAgentWebSocketUrl,
  sendChatMessageWs,
  type RemediationChatParams,
  type Workspace,
  type ChatMessagePayloadBase,
} from '../api-utils/agent';
import { getAccessToken } from './auth/getAccessToken';
import type { S3LogEntry } from '../types/logs';

// ─── Types ─────────────────────────────────────────────────────────────────

interface RemediationPlanStep {
  step_id: string;
  title: string;
  action: string;
  inputs?: Record<string, unknown>;
  success_criteria?: string;
  depends_on?: string[];
  next_step?: string | null;
}

interface RemediationPlan {
  id: string;
  steps: RemediationPlanStep[];
  meta?: Record<string, unknown>;
}

/** Step execution in workspace.state_machine[plan_id].steps */
interface StateMachineStep {
  step_id: string;
  title?: string;
  status?: string;
  started_at?: string;
  finished_at?: string;
  error?: string | null;
  action_log?: unknown[];
  [key: string]: unknown;
}

/** Plan definition in workspace.plan[plan_id] */
interface WorkspacePlanDef {
  id: string;
  steps: RemediationPlanStep[];
  meta?: Record<string, unknown>;
}

/** State machine for one plan in workspace.state_machine[plan_id] */
interface StateMachinePlan {
  plan_id: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  steps: StateMachineStep[];
  [key: string]: unknown;
}

interface RemediationsPanelProps {
  threatEventId: string;
  s3Logs: S3LogEntry[];
  s3LogsLoading?: boolean;
  /** Threat classification for the completion notification (e.g. incident.type). */
  threatClassification?: string;
  onRemediationComplete?: () => void;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const PLAN_CACHE_KEY = 'irn:tool_rs:pes/generate_plan';

function extractPlanFromWorkspace(workspace: Workspace | null): RemediationPlan | null {
  if (!workspace) return null;
  const cache = workspace['cache'] as Record<string, unknown> | undefined;
  if (!cache) return null;
  const entry = cache[PLAN_CACHE_KEY] as { output?: { plan?: RemediationPlan } } | undefined;
  return entry?.output?.plan ?? null;
}

/** Pick the latest plan by state_machine[plan_id].created_at (ISO string), then by key order. */
function getLastPlanId(workspace: Workspace | null): string | null {
  if (!workspace) return null;
  const plan = workspace['plan'] as Record<string, WorkspacePlanDef> | undefined;
  const sm = workspace['state_machine'] as Record<string, StateMachinePlan> | undefined;
  if (!plan || typeof plan !== 'object') return null;
  const ids = Object.keys(plan);
  if (ids.length === 0) return null;
  if (ids.length === 1) return ids[0];
  if (!sm || typeof sm !== 'object') return ids[ids.length - 1];
  let latest = ids[0];
  let latestAt = (sm[latest]?.created_at as string) || '';
  for (let i = 1; i < ids.length; i++) {
    const id = ids[i];
    const at = (sm[id]?.created_at as string) || '';
    if (at > latestAt) {
      latestAt = at;
      latest = id;
    }
  }
  return latest;
}

/** True when plan is considered fully complete (all steps or plan status completed). */
function isPlanComplete(workspace: Workspace | null, planId: string | null): boolean {
  if (!workspace || !planId) return false;
  const sm = workspace['state_machine'] as Record<string, StateMachinePlan> | undefined;
  const planSm = sm?.[planId];
  if (!planSm?.steps || !Array.isArray(planSm.steps)) return false;
  if (planSm.status === 'completed') return true;
  return planSm.steps.every((s) => s.status === 'completed');
}

// ─── Component ─────────────────────────────────────────────────────────────

const RemediationsPanel: React.FC<RemediationsPanelProps> = ({
  threatEventId,
  s3Logs,
  s3LogsLoading = false,
  threatClassification,
  onRemediationComplete,
}) => {
  const { org } = useWorkspace();
  const [remediation, setRemediation] = useState<RemediationItem | null>(null);
  const [loadingRemediation, setLoadingRemediation] = useState(true);
  const [chatParams, setChatParams] = useState<RemediationChatParams | null>(null);
  const [plan, setPlan] = useState<RemediationPlan | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generatingStatus, setGeneratingStatus] = useState('');
  const [committed, setCommitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [liveUpdates, setLiveUpdates] = useState<string[]>([]);
  const [liveUpdatesExpanded, setLiveUpdatesExpanded] = useState(false);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const messageHandlerRef = useRef<(data: unknown) => void>(() => {});
  const remediationCompleteDoneRef = useRef(false);
  const pendingPlanRef = useRef<{
    resolve: (plan: RemediationPlan | null) => void;
    reject: (err: Error) => void;
    rem: RemediationItem;
    params: RemediationChatParams;
    logsMessage: string;
    payload: ChatMessagePayloadBase;
    retried: boolean;
  } | null>(null);

  const handleAgentTextMessage = useCallback(async () => {
    const p = pendingPlanRef.current;
    if (!p || !getWorkspace().portfolio || !getWorkspace().org) return;
      const { resolve, reject, rem, params, logsMessage, payload, retried } = p;
    try {
      setGeneratingStatus('Checking for remediation plan…');
      const workspace = await getActiveWorkspace(getWorkspace().portfolio, getWorkspace().org, rem._id, params.activeThread ?? '');
      const foundPlan = extractPlanFromWorkspace(workspace);
      if (foundPlan) {
        pendingPlanRef.current = null;
        resolve(foundPlan);
        return;
      }
      if (!retried) {
        pendingPlanRef.current = { ...p, retried: true };
        const ws = wsRef.current;
        if (ws?.readyState === WebSocket.OPEN) {
          await sendChatMessageWs(ws, logsMessage, payload, getAccessToken);
        } else {
          pendingPlanRef.current = null;
          resolve(null);
        }
        return;
      }
      pendingPlanRef.current = null;
      resolve(null);
    } catch (e) {
      pendingPlanRef.current = null;
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  }, []);

  messageHandlerRef.current = (data: unknown) => {
    const d = data as { _type?: string; _out?: { content?: unknown } } | null;
    if (!d) return;
    if (d._type === 'transient' && d._out?.content != null) {
      setLiveUpdates((prev) => [...prev, String(d._out!.content)]);
    }
    if (d._type === 'text' && pendingPlanRef.current) {
      handleAgentTextMessage();
    }
    if (committed && chatParams?.activeThread && remediation && getWorkspace().portfolio && getWorkspace().org) {
      getActiveWorkspace(getWorkspace().portfolio, getWorkspace().org, remediation._id, chatParams.activeThread).then(
        (ws) => ws != null && setWorkspace(ws)
      );
    }
  };

  const ensureAgentWebSocket = useCallback((): Promise<WebSocket> => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return Promise.resolve(wsRef.current);
    if (wsRef.current) wsRef.current.close();
    return new Promise((resolve, reject) => {
      try {
        const url = getAgentWebSocketUrl();
        const wsUrl = url.replace(/^http/, 'ws');
        const socket = new WebSocket(wsUrl);
        wsRef.current = socket;
        socket.onopen = () => resolve(socket);
        socket.onerror = () => reject(new Error('WebSocket connection failed'));
        socket.onclose = () => { wsRef.current = null; };
        socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            messageHandlerRef.current(data);
          } catch { /* ignore */ }
        };
      } catch (e) { reject(e); }
    });
  }, []);

  useEffect(() => {
    return () => {
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    };
  }, []);

  // ── Initial load ──────────────────────────────────────────────────────────

  const loadRemediation = useCallback(async () => {
    if (!config.apiBase || !getWorkspace().portfolio || !getWorkspace().org) {
      setLoadingRemediation(false);
      return;
    }
    setLoadingRemediation(true);
    setError(null);
    try {
      const rem = await getRemediationByThreatEventId(threatEventId);
      setRemediation(rem);
      if (rem) {
        const params = await getRemediationChatParams(getWorkspace().portfolio, getWorkspace().org, rem._id);
        setChatParams(params);
        if (params.activeThread) {
          const ws = await getActiveWorkspace(getWorkspace().portfolio, getWorkspace().org, rem._id, params.activeThread);
          const existingPlan = extractPlanFromWorkspace(ws);
          if (existingPlan) setPlan(existingPlan);
          const planObj = ws?.['plan'] as Record<string, unknown> | undefined;
          if (planObj && typeof planObj === 'object' && Object.keys(planObj).length > 0) {
            setCommitted(true);
            setWorkspace(ws);
          }
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingRemediation(false);
    }
  }, [threatEventId]);

  useEffect(() => {
    loadRemediation();
  }, [loadRemediation, org]);

  useEffect(() => {
    remediationCompleteDoneRef.current = false;
  }, [threatEventId, remediation?._id]);

  // Fallback: when committed but no workspace yet, fetch after 3s in case no WS message arrived
  useEffect(() => {
    if (!committed || workspace || !remediation || !chatParams?.activeThread || !getWorkspace().portfolio || !getWorkspace().org) return;
    const t = setTimeout(() => {
      getActiveWorkspace(getWorkspace().portfolio!, getWorkspace().org!, remediation._id, chatParams.activeThread!).then(
        (w) => w != null && setWorkspace(w)
      );
    }, 3000);
    return () => clearTimeout(t);
  }, [committed, workspace, remediation, chatParams?.activeThread]);

  // ── Core agent interaction ─────────────────────────────────────────────────

  const sendLogsAndFetchPlan = useCallback(
    async (rem: RemediationItem): Promise<RemediationPlan | null> => {
      const params = await getRemediationChatParams(getWorkspace().portfolio, getWorkspace().org, rem._id);
      setChatParams(params);

      const payload = buildChatMessagePayload({
        portfolio: getWorkspace().portfolio,
        org: getWorkspace().org,
        entity_type: params.entity_type,
        entity_id: params.entity_id,
        thread: params.activeThread,
        core: REMEDIATION_AGENT_CORE,
        case_group: REMEDIATION_CASE_GROUP,
      });

      const logsMessage = s3Logs.map((e) => e.chunk).join('\n');

      setGeneratingStatus('Connecting to agent…');
      const ws = await ensureAgentWebSocket();
      setGeneratingStatus('Sending threat data to remediation agent…');
      const sent = await sendChatMessageWs(ws, logsMessage, payload, getAccessToken);
      if (!sent) throw new Error('WebSocket not ready. Try again.');

      if (!params.activeThread) return null;

      setGeneratingStatus('Waiting for agent response…');
      return new Promise<RemediationPlan | null>((resolve, reject) => {
        const timeoutMs = 120_000;
        const timeoutId = setTimeout(() => {
          if (pendingPlanRef.current) {
            pendingPlanRef.current = null;
            reject(new Error('Agent did not respond in time. Try again.'));
          }
        }, timeoutMs);
        const resolveOnce = (plan: RemediationPlan | null) => {
          clearTimeout(timeoutId);
          resolve(plan);
        };
        const rejectOnce = (err: Error) => {
          clearTimeout(timeoutId);
          reject(err);
        };
        pendingPlanRef.current = {
          resolve: resolveOnce,
          reject: rejectOnce,
          rem,
          params,
          logsMessage,
          payload,
          retried: false,
        };
      });
    },
    [s3Logs, ensureAgentWebSocket]
  );

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleGeneratePlan = useCallback(async () => {
    if (!config.apiBase || !getWorkspace().portfolio || !getWorkspace().org) return;
    setGenerating(true);
    setError(null);
    setPlan(null);
    setCommitted(false);
    setLiveUpdates([]);
    try {
      let rem = remediation;
      if (!rem) {
        setGeneratingStatus('Creating remediation record…');
        const created = await createRemediationDoc(threatEventId, {
          threat_event_id: threatEventId,
          status: 'pending',
        });
        if (!created) throw new Error('Failed to create remediation document.');
        // Re-fetch by threat_event_id to confirm persistence
        const refetched = await getRemediationByThreatEventId(threatEventId);
        rem = refetched ?? created;
        setRemediation(rem);
      }

      const foundPlan = await sendLogsAndFetchPlan(rem);
      if (foundPlan) {
        setPlan(foundPlan);
      } else {
        setError('The agent processed the request but no plan was returned yet. Try again.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGenerating(false);
      setGeneratingStatus('');
    }
  }, [remediation, threatEventId, sendLogsAndFetchPlan]);

  const handleCommitToPlan = useCallback(async () => {
    if (!chatParams || !remediation) return;
    setGenerating(true);
    setError(null);
    try {
      const payload = buildChatMessagePayload({
        portfolio: getWorkspace().portfolio,
        org: getWorkspace().org,
        entity_type: chatParams.entity_type,
        entity_id: chatParams.entity_id,
        thread: chatParams.activeThread,
        core: REMEDIATION_AGENT_CORE,
        case_group: REMEDIATION_CASE_GROUP,
      });
      const ws = await ensureAgentWebSocket();
      const sent = await sendChatMessageWs(ws, 'ok, commit to plan', payload, getAccessToken);
      if (!sent) throw new Error('WebSocket not ready. Try again.');
      setCommitted(true);
      setWorkspace(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGenerating(false);
    }
  }, [chatParams, remediation, ensureAgentWebSocket]);

  // ── Remediation complete: run APIs, create notification, refetch, callback once ─
  const lastPlanId = getLastPlanId(workspace);
  const planComplete = isPlanComplete(workspace, lastPlanId);
  useEffect(() => {
    if (!planComplete || remediationCompleteDoneRef.current || !remediation || !lastPlanId) return;
    const sm = workspace?.state_machine as Record<string, StateMachinePlan> | undefined;
    const planSm = sm?.[lastPlanId];
    remediationCompleteDoneRef.current = true;
    const startedAt = (planSm?.created_at as string) || new Date().toISOString();
    const steps = planSm?.steps ?? [];
    const totalSteps = steps.length;
    const lastStep = steps.length > 0 ? steps[steps.length - 1] : null;
    const finishedAt = (lastStep?.finished_at as string) || new Date().toISOString();

    (async () => {
      await updateThreatEvent(threatEventId, {
        status: 'remediated',
        started_at: startedAt,
        finished_at: finishedAt,
      });
      await updateRemediation(remediation._id, {
        status: 'completed',
        operations: steps as unknown[],
      });
      try {
        // Deduplicate in dev (StrictMode remount) and WS refreshes.
        const dedupeKey = `arbitium:notif:remediation_completed:${remediation._id}:${lastPlanId}`;
        const canUseStorage = typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
        const alreadySent = canUseStorage ? window.sessionStorage.getItem(dedupeKey) === '1' : false;
        if (!alreadySent) {
          if (canUseStorage) window.sessionStorage.setItem(dedupeKey, '1'); // reserve before await
          try {
            await createNotification({
              type: 'Remediation',
              title: 'Remediation Completed',
              details: `${threatClassification ?? 'Threat'} remediated with ${totalSteps} step${totalSteps !== 1 ? 's' : ''}`,
              source: 'Arbitium AI agent',
              time: new Date().toISOString(),
              status: 'unread',
            });
          } catch (e) {
            // Allow retry in this session if creation failed.
            if (canUseStorage) window.sessionStorage.removeItem(dedupeKey);
            throw e;
          }
        }
      } catch (e) {
        console.warn('Remediation completion notification failed:', e);
      }
      onRemediationComplete?.();
    })();
  }, [planComplete, lastPlanId, remediation, workspace, threatEventId, threatClassification, onRemediationComplete]);

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loadingRemediation) {
    return (
      <div className="flex items-center gap-2 text-gray-400 py-8 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading remediation data…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-start gap-2 text-red-400 text-sm p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {/* No remediation yet */}
      {!remediation && !generating && (
        <div className="text-center py-8">
          <div className="w-14 h-14 bg-arbitium-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ClipboardCheck className="w-7 h-7 text-arbitium-primary" />
          </div>
          <p className="text-gray-400 mb-1">No remediation plan found for this threat.</p>
          <p className="text-gray-600 text-sm mb-6">
            Generate a plan to receive AI-driven step-by-step remediation instructions.
          </p>
          <button
            onClick={handleGeneratePlan}
            disabled={s3LogsLoading || s3Logs.length === 0}
            className="px-5 py-2.5 bg-gradient-to-r from-arbitium-primary to-arbitium-accent text-white text-sm font-medium rounded-xl flex items-center gap-2 mx-auto hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Zap className="w-4 h-4" />
            Generate Remediation Plan
          </button>
          {s3Logs.length === 0 && !s3LogsLoading && (
            <p className="text-gray-600 text-xs mt-2">Waiting for threat log data…</p>
          )}
        </div>
      )}

      {/* Generating progress */}
      {generating && (
        <div className="flex flex-col items-center py-8 gap-3">
          <Loader2 className="w-8 h-8 text-arbitium-primary animate-spin" />
          <p className="text-gray-400 text-sm">{generatingStatus || 'Working…'}</p>
          {liveUpdates.length > 0 && (
            <div className="mt-4 w-full text-left">
              {!liveUpdatesExpanded ? (
                <div className="flex items-center gap-2 text-xs text-gray-300">
                  <span className="flex-1 truncate">{liveUpdates[liveUpdates.length - 1]}</span>
                  <button
                    type="button"
                    onClick={() => setLiveUpdatesExpanded(true)}
                    className="shrink-0 p-1 text-arbitium-accent hover:bg-arbitium-accent/10 rounded"
                    aria-label="Show all updates"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="max-h-40 overflow-y-auto space-y-1 text-xs text-gray-300">
                  {liveUpdates.map((msg, idx) => (
                    <div key={idx} className="flex items-start gap-1.5">
                      <span className="text-arbitium-accent shrink-0">•</span>
                      <span>{msg}</span>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setLiveUpdatesExpanded(false)}
                    className="flex items-center gap-1 mt-2 text-arbitium-accent hover:underline"
                  >
                    <ChevronUp className="w-3 h-3" /> Show less
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Committed: progress bar + step list (or waiting for workspace) */}
      {committed && !generating && (
        <>
          {!workspace ? (
            <div className="flex items-center gap-3 text-white p-5 bg-arbitium-primary/15 border border-arbitium-primary/30 rounded-xl">
              <Loader2 className="w-6 h-6 shrink-0 animate-spin text-arbitium-accent" />
              <span className="font-medium text-sm">Preparing remediation</span>
            </div>
          ) : (
            (() => {
              const planId = getLastPlanId(workspace);
              const planDef = planId ? (workspace['plan'] as Record<string, WorkspacePlanDef>)?.[planId] : null;
              const sm = workspace['state_machine'] as Record<string, StateMachinePlan> | undefined;
              const planSm = planId ? sm?.[planId] : null;
              const steps = planDef?.steps ?? [];
              const smSteps = planSm?.steps ?? [];
              const completedCount = smSteps.filter((s) => s.status === 'completed').length;
              const totalSteps = steps.length;
              const progressPercent = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;
              const complete = isPlanComplete(workspace, planId);
              return (
                <div className="space-y-3">
                  {complete && (
                    <div className="flex items-center gap-2 text-green-400 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                      <CheckCircle className="w-4 h-4 shrink-0" />
                      Remediation complete
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{completedCount}/{totalSteps} steps</span>
                  </div>
                  <div className="w-full bg-arbitium-darker rounded-full h-2">
                    <div
                      className="h-2 rounded-full bg-gradient-to-r from-arbitium-primary to-arbitium-accent transition-all duration-300"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    {steps.map((step, idx) => {
                      const smStep = smSteps.find((s) => String(s.step_id) === String(step.step_id));
                      const status = smStep?.status ?? 'pending';
                      const isCompleted = status === 'completed';
                      const isError = status === 'error';
                      const isPending = status === 'pending' || !status;
                      return (
                        <div
                          key={step.step_id}
                          className={`flex items-center gap-2 py-2 px-3 rounded-lg text-sm ${
                            isCompleted ? 'bg-green-500/5' : isError ? 'bg-red-500/10' : 'bg-arbitium-darker/60'
                          }`}
                        >
                          <span className="w-5 h-5 flex items-center justify-center shrink-0">
                            {isCompleted && <CheckCircle className="w-4 h-4 text-green-400" />}
                            {isError && <AlertTriangle className="w-4 h-4 text-red-400" />}
                            {isPending && <Circle className="w-4 h-4 text-gray-500" />}
                            {!isCompleted && !isError && !isPending && (
                              <Loader2 className="w-4 h-4 text-arbitium-accent animate-spin" />
                            )}
                          </span>
                          <span className="text-white font-medium truncate flex-1">{step.title}</span>
                          <span className={`text-xs shrink-0 ${
                            isCompleted ? 'text-green-400' : isError ? 'text-red-400' : 'text-gray-500'
                          }`}>
                            {isCompleted ? 'Completed' : isError ? 'Error' : status || 'Pending'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {liveUpdates.length > 0 && (
                    <div className="pt-2 border-t border-arbitium-primary/10">
                      <p className="text-xs text-gray-500 mb-1">Agent updates</p>
                      {!liveUpdatesExpanded ? (
                        <div className="flex items-center gap-2 text-xs text-gray-300">
                          <span className="flex-1 truncate">{liveUpdates[liveUpdates.length - 1]}</span>
                          <button
                            type="button"
                            onClick={() => setLiveUpdatesExpanded(true)}
                            className="shrink-0 p-1 text-arbitium-accent hover:bg-arbitium-accent/10 rounded"
                            aria-label="Show all updates"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="max-h-40 overflow-y-auto space-y-1 text-xs text-gray-300">
                          {liveUpdates.map((msg, idx) => (
                            <div key={idx} className="flex items-start gap-1.5">
                              <span className="text-arbitium-accent shrink-0">•</span>
                              <span>{msg}</span>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => setLiveUpdatesExpanded(false)}
                            className="flex items-center gap-1 mt-2 text-arbitium-accent hover:underline"
                          >
                            <ChevronUp className="w-3 h-3" /> Show less
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()
          )}
        </>
      )}

      {/* Plan display (only when not committed) */}
      {plan && !generating && !committed && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500 font-mono">
              {`Plan ${plan.id}${plan.meta?.strategy ? ` · ${String(plan.meta.strategy)}` : ''}`}
            </p>
          </div>

          {/* Steps */}
          <div className="space-y-2">
            {plan.steps.map((step, idx) => (
              <div
                key={step.step_id}
                className="bg-arbitium-darker/60 border border-arbitium-primary/10 rounded-xl p-4"
              >
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-arbitium-primary/20 border border-arbitium-primary/40 text-arbitium-accent text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm">{step.title}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded bg-arbitium-accent/10 text-arbitium-accent border border-arbitium-accent/20 font-mono">
                      {step.action}
                    </span>

                    {step.inputs && Object.keys(step.inputs).length > 0 && (
                      <div className="mt-2 space-y-1">
                        {Object.entries(step.inputs)
                          .filter(([, v], idx, arr) => {
                            const normalized = String(v ?? '').trim().toLowerCase();
                            return (
                              idx ===
                              arr.findIndex(([, other]) => String(other ?? '').trim().toLowerCase() === normalized)
                            );
                          })
                          .map(([k, v]) => (
                          <div key={k} className="flex gap-2 text-xs">
                            <span className="text-gray-500 shrink-0">{k}:</span>
                            <span className="text-gray-300 font-mono break-all">{String(v)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {step.success_criteria && (
                      <div className="mt-2 flex items-start gap-1.5 text-xs text-green-400/80">
                        <CheckCircle className="w-3 h-3 shrink-0 mt-0.5" />
                        {step.success_criteria}
                      </div>
                    )}
                  </div>

                  {step.next_step != null && (
                    <ChevronRight className="w-4 h-4 text-gray-600 shrink-0 mt-1" />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Plan actions */}
          {!committed && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={handleCommitToPlan}
                disabled={generating}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-gradient-to-r from-green-600 to-emerald-500 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <CheckCircle className="w-3.5 h-3.5" />
                Excecute remediation plan
              </button>
            </div>
          )}
        </div>
      )}

      {/* Remediation exists but no plan yet and not currently generating */}
      {remediation && !plan && !generating && !error && (
        <div className="text-center py-6">
          <p className="text-gray-400 text-sm mb-4">
            Remediation record found. Generate a plan to get step-by-step instructions.
          </p>
          <button
            onClick={handleGeneratePlan}
            disabled={s3Logs.length === 0}
            className="px-5 py-2.5 bg-gradient-to-r from-arbitium-primary to-arbitium-accent text-white text-sm font-medium rounded-xl flex items-center gap-2 mx-auto hover:opacity-90 transition-all disabled:opacity-50"
          >
            <Zap className="w-4 h-4" />
            Generate Remediation Plan
          </button>
        </div>
      )}
    </div>
  );
};

export default RemediationsPanel;
